import { colors, fontStyles, sansFontFamily } from '@frontend/constants';
import { useCallback } from '@frontend/react';
import { InputLabel, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import { useStripeRegisterReady, type StripeElementKind } from '@frontend/web/components';
import type { StripeCardNumberElementOptions } from '@stripe/stripe-js';
import type { ComponentType } from 'react';

const FIELD_CLASS_NAMES = safeCss(
  'p-md rounded-md border-1 border-borderTertiary focus-within:border-textPrimary focus:border-textPrimary bg-white',
);

const DEFAULT_OPTIONS: StripeCardNumberElementOptions = {
  style: {
    base: {
      '::placeholder': {
        color: colors.textTertiary,
      },
      color: colors.textPrimary,
      ...fontStyles.rem['p-md'],
      fontFamily: sansFontFamily[0],
      fontSmoothing: 'antialiased',
    },
    invalid: {
      color: colors.textError,
      iconColor: colors.textError,
    },
  },
};

type StripeElementComponent<TEvent> = ComponentType<{
  className?: string;
  onChange?: (event: TEvent) => void;
  onReady?: () => void;
  options?: StripeCardNumberElementOptions;
}>;

type CardFieldConfig<TEvent> = {
  ElementComponent: StripeElementComponent<TEvent>;
  elementKind: StripeElementKind;
  label: string;
  options?: StripeCardNumberElementOptions;
  placeholder?: string;
  wrapperClassName: string;
};

export const createCardField = <TEvent,>({
  ElementComponent,
  elementKind,
  label,
  options = DEFAULT_OPTIONS,
  placeholder,
  wrapperClassName,
}: CardFieldConfig<TEvent>) => {
  const CardField = ({ onChange }: { onChange: (event: TEvent) => void }) => {
    const registerReady = useStripeRegisterReady();
    const handleReady = useCallback(() => registerReady?.(elementKind), [registerReady]);

    return (
      <YStack className={wrapperClassName}>
        <InputLabel label={label} textProps={{ color: 'tertiary' }} />
        <ElementComponent
          className={FIELD_CLASS_NAMES}
          onChange={onChange}
          onReady={handleReady}
          options={{ ...options, placeholder }}
        />
      </YStack>
    );
  };
  return CardField;
};
